import { useMemo } from 'react';
import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';

import { ApiContext } from './api-context';
import { isValidToken, useAuthContext, getUser } from '../auth';

type Props = {
  children: React.ReactNode;
};
/**
 * Provides an Axios instance with authentication and token refreshing capabilities.
 * @param children - The child components to be wrapped by the ApiProvider.
 */
export function ApiProvider({ children }: Props) {
  const { logout } = useAuthContext();

  /**
   * Creates an Axios instance with a base URL and interceptors for adding access tokens to headers and handling token refreshing.
   */
  const axiosInstance = useMemo(() => {
    const instance: AxiosInstance = axios.create({
      validateStatus: (status) => (status >= 200 && status < 300) || status === 304,
    });

    // Request interceptor: read the *live* token from the OIDC store (reading from React state was
    // the AC-2115 desync — stale instance closures held an old token). If there is no usable
    // session, log out and abort instead of firing an unauthenticated request that 401s in a loop.
    // Pre-session flows (registration, password/MFA recovery, anonymous email-link replies) opt out
    // via the createUsePublicApi* factories, which set config.isPublic — those fire without a token.
    instance.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
      const current = await getUser();
      const access_token = current?.access_token ?? null;
      const tokenValid = !!access_token && isValidToken(access_token);

      if (!config.isPublic && !tokenValid) {
        logout(); // single-flight guarded in AuthProvider
        return Promise.reject(new axios.Cancel('Session expired — logging out'));
      }

      if (tokenValid) {
        config.headers.Authorization = `Bearer ${access_token}`;
      }
      config.headers['AC-Request-Id'] = crypto.randomUUID();

      return config;
    });

    return instance;
  }, [logout]);

  return <ApiContext.Provider value={axiosInstance}>{children}</ApiContext.Provider>;
}
